import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { UncontrolledRecipientWellProps } from 'owa-readwrite-recipient-well-fabric/lib/store/schema/UncontrolledRecipientWellProps';
import { PlaceholderReadWriteRecipientWell } from './PlaceholderReadWriteRecipientWell';
import { IFocusable } from '../types/IFocusable';
import { getStore } from '../store/store';
import { setIsLoaded } from '../actions/setIsLoaded';
import sleep from 'owa-sleep';
import { getReactSuspense } from '../utils/getReactSuspense';
import { LazyUncontrolledRecipientWell } from './LazyUncontrolledRecipientWell';

/**
 * Since while this is feature-flagged the Recipient well is already loaded in the same
 * bundle, but we want to simulate async well loading the module in the background, we
 * artificially simulate async loading here.
 */
const FAKE_LOAD_DELAY_MS = 1000;

/**
 * Manual version of React.Suspense that passes focus between the suspended component and the
 * actual component on load.
 */
const AsyncReadWriteRecipientWellInner = (
    props: UncontrolledRecipientWellProps,
    ref: React.Ref<IFocusable>
) => {
    ///////////////////////
    // Import Management //
    ///////////////////////
    const numRecipients = props.viewState.recipients.length;

    // We should immediately import if there are recipients already on the well
    // TODO change the initial state to numRecipients != 0 when we are no longer faking a
    // suspended component.
    const [shouldTriggerImport, setShouldTriggerImport] = React.useState<boolean>(false);

    // Load the recipient.
    const triggerRecipientWellLoad = React.useCallback(async () => {
        await sleep(FAKE_LOAD_DELAY_MS);
        if (!shouldTriggerImport) {
            setShouldTriggerImport(true);
        }
    }, [shouldTriggerImport, setShouldTriggerImport]);

    // Load the recipient well if we're given recipients
    React.useEffect(() => {
        if (numRecipients !== 0) {
            triggerRecipientWellLoad();
        }
    }, [numRecipients !== 0]);

    //////////////////////
    // Focus Management //
    //////////////////////

    // Track focus in the placeholder component
    const [placeholderHasFocus, setPlaceholderHasFocus] = React.useState<boolean>(false);
    const setPlaceholderFocused = React.useCallback(() => {
        setPlaceholderHasFocus(true);
    }, []);
    const setPlaceholderBlurred = React.useCallback(() => {
        setPlaceholderHasFocus(false);
    }, []);

    // Wrap focus() on the ref
    const childRef = React.useRef<IFocusable>();
    const placeholderChildRef = React.useRef<HTMLInputElement>();
    React.useImperativeHandle(
        ref,
        () => ({
            focus: () =>
                childRef.current
                    ? childRef.current.focus()
                    : placeholderChildRef.current && placeholderChildRef.current.focus(),
        }),
        [childRef, placeholderChildRef]
    );

    // After the suspended component has been mounted, pass focus back to it
    // if the fallback component still has focus.
    const onLoadCompleted = React.useCallback(
        (childRefValue: IFocusable | null) => {
            childRef.current = childRefValue;
            // inform other wells that the script has loaded from suspension
            setIsLoaded();
            if (childRefValue && placeholderHasFocus) {
                // clear focus on the placeholder so if there is a re-render we don't
                // re-trigger this logic
                setPlaceholderBlurred();
                // force focus to the new child -- the actual well
                childRefValue.focus();
            }
        },
        [placeholderHasFocus, childRef]
    );

    ///////////////
    // Rendering //
    ///////////////

    const wellLabel = props.onRenderWellLabel?.(props.recipientType);
    const recipientNames = props.viewState.recipients.map(
        recipient => recipient.persona?.EmailAddress?.Name || null
    );

    const placeholderWell = (
        <PlaceholderReadWriteRecipientWell
            ref={placeholderChildRef}
            viewState={props.viewState}
            onImportTriggered={triggerRecipientWellLoad}
            onFocus={setPlaceholderFocused}
            onBlur={setPlaceholderBlurred}
            recipientNames={recipientNames}
            wellLabel={wellLabel}
        />
    );

    // Use a mockable accessor to get react suspense -- otherwise this is not mockable.
    const Suspense = getReactSuspense();
    return getStore().isLoaded || shouldTriggerImport ? (
        <Suspense fallback={placeholderWell}>
            <LazyUncontrolledRecipientWell ref={onLoadCompleted} {...props} />
        </Suspense>
    ) : (
        placeholderWell
    );
};

export const AsyncReadWriteRecipientWell = observer(AsyncReadWriteRecipientWellInner, {
    forwardRef: true,
});
